import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { Button, Pagination } from 'antd';
import { Loading } from '@components/common';
import { isAdminRoleSelector } from '@store/common/selectors';
import Table from '@components/common/Table';
import { useLocationQuery } from '@hooks';
import columns from './columns';
import { Title, PageHeader, TableWrapper, PageButtons, Wrapper } from './styled';
import { getMetricInfo } from '../constants';
import { TableData } from '../summary/components/AlertTable/utils/formatTableData';
import AlertForm from '../form';
import { FormActionContext } from '../form/context';
import { Confirmation, convertValue } from '../shared';
import useFetchAlertEvents from '../summary/hooks/useFetchAlertEvents';
import ExportButton from './ExportButton';
import { useNavigate } from 'react-router-dom';

const pageSize = 500;

const Events = () => {
  const navigate = useNavigate();
  const query = useLocationQuery();
  const alertId = parseInt(query.get('alertId'), 10);
  const page = parseInt(query.get('page'), 10) || 1;

  const { isFetching: loading, data: alert } = useFetchAlertEvents({ alertId, pageSize, page });

  const [visible, setVisible] = useState<boolean>(false);
  const [editAlert, setEditAlert] = useState(alert);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState({});
  const isAdmin = useSelector(isAdminRoleSelector);
  const onCancel = useCallback(() => {
    setVisible(false);
  }, [setVisible]);
  const onClick = useCallback(() => {
    setEditAlert(alert);
    setVisible(true);
  }, [setVisible, alert, setEditAlert]);

  const onCancelConfirmation = useCallback(() => {
    setAlertToDelete({});
    setConfirmationVisible(false);
  }, [setAlertToDelete, setConfirmationVisible]);

  const onDelete = useCallback(
    (alert) => {
      setConfirmationVisible(true);
      setAlertToDelete(alert);
    },
    [setConfirmationVisible, setAlertToDelete],
  );

  const onEdit = useCallback(
    (alert) => {
      setVisible(true);
      setEditAlert(alert);
    },
    [setVisible, setEditAlert],
  );

  const onClickPage = useCallback(
    (p) => {
      navigate(`${window.location.pathname}?alertId=${alertId}&page=${p}`);
    },
    [alertId, navigate],
  );

  if (loading) {
    return <Loading />;
  }

  if (!alert) {
    return null;
  }

  const { name, metric, direction, threshold, filter, samples } = alert;
  const metricsInfo = getMetricInfo();
  const metricInfo = metricsInfo[metric];
  const condition = `${metricInfo.name} ${direction} ${convertValue(threshold, metricInfo.units)} ${metricInfo.units}`;

  const alertData: TableData[] = [
    {
      name,
      filter,
      id: `alert-${alert.id}`,
      condition,
      sample: `${samples} ${metricInfo.cm_label}`,
      time: get(alert, 'events.0.time', '-'),
      actions: true,
      source: alert,
    },
  ];

  const eventsData: TableData[] = get(alert, 'events', []).map((event) => ({
    ...event,
    id: `event-${event.id}`,
    name,
    metric,
    direction,
    threshold,
    filter,
    sample: `${event.samples} ${metricInfo.cm_label}`,
    condition: `${convertValue(event.value, metricInfo.units)} ${metricInfo.units}`,
    source: event,
  }));

  return (
    <FormActionContext.Provider
      value={{
        formVisible: visible,
        actions: {
          onEdit,
          onDelete,
          onCancel,
        },
      }}
    >
      <Wrapper>
        <PageHeader>
          <Title>Events List for {alert.name}</Title>
          <PageButtons>
            {isAdmin && (
              <Button type="primary" onClick={onClick}>
                Edit Alert
              </Button>
            )}
            <ExportButton alertData={alertData} eventsData={eventsData} columns={columns} />
          </PageButtons>
        </PageHeader>
        <TableWrapper>
          <div style={{ height: 125 }}>
            <Table columns={columns as any} data={alertData} fixed />
          </div>
          <Table fixed columns={columns as any} data={eventsData} headerHeight={0} />
        </TableWrapper>
        <Pagination
          total={alert.eventCount}
          pageSize={pageSize}
          pageSizeOptions={[pageSize]}
          defaultCurrent={page}
          onChange={onClickPage}
        />
        <AlertForm
          key={JSON.stringify(editAlert)}
          alert={editAlert}
          visible={visible}
          onCancel={onCancel}
        />
        <Confirmation
          visible={confirmationVisible}
          alert={alertToDelete as ManualAlert}
          onCancel={onCancelConfirmation}
        />
      </Wrapper>
    </FormActionContext.Provider>
  );
};

export default Events;
